// ---------------------------------------------------------
// Datei: AllgemeineUploads.tsx
// Zweck: Generischer Foto/Datei-Upload ohne Phasenzuordnung
// Besonderheiten:
// - Nutzt FileUploadModal (Kamera + Galerie, Multi-Upload)
import { getEnv } from "../utils/env";
// - Optionale Notiz pro Upload-Batch
// - Upload via driveUploadService -> Backend /api/drive -> Google Drive
// - Upload-Historie in localStorage
// Stand: Maerz 2026
// ---------------------------------------------------------

import React, { useState, useEffect } from 'react';
import { useToast } from '../contexts/ToastContext';
import FileUploadModal, { UploadFile } from './FileUploadModal';
import { uploadFilesToDrive, dataUrlToFile, DriveUploadedFile } from '../services/driveUploadService';

interface AllgemeineUploadsProps {
  dealName: string;
}

interface UploadHistoryEntry {
  id: string;
  timestamp: string;
  notiz: string;
  fileCount: number;
  files: DriveUploadedFile[];
}

const HISTORY_KEY_PREFIX = 'allgemeine-uploads-history-';

function getHistoryKey(dealName: string): string {
  return `${HISTORY_KEY_PREFIX}${dealName.replace(/\s+/g, '-')}`;
}

function loadHistory(dealName: string): UploadHistoryEntry[] {
  try {
    const raw = localStorage.getItem(getHistoryKey(dealName));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveHistory(dealName: string, history: UploadHistoryEntry[]) {
  localStorage.setItem(getHistoryKey(dealName), JSON.stringify(history));
}

const AllgemeineUploads: React.FC<AllgemeineUploadsProps> = ({ dealName }) => {
  const { showToast } = useToast();

  // State
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [notiz, setNotiz] = useState('');
  const [pendingFiles, setPendingFiles] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const [history, setHistory] = useState<UploadHistoryEntry[]>([]);

  // Load history on mount
  useEffect(() => {
    setHistory(loadHistory(dealName));
  }, [dealName]);

  // Handle files selected from FileUploadModal
  const handleFilesSelected = (files: UploadFile[]) => {
    setPendingFiles(prev => [...prev, ...files]);
    setShowUploadModal(false);
  };

  // Remove a pending file
  const removePendingFile = (fileId: string) => {
    setPendingFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // Upload all pending files to Google Drive
  const handleUpload = async () => {
    if (pendingFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(`0/${pendingFiles.length} hochgeladen...`);

    try {
      // Convert UploadFiles to File objects
      const filesToUpload: File[] = pendingFiles.map((uf) => {
        if (uf.file) return uf.file;
        // Camera photos come as dataURL - convert to File
        return dataUrlToFile(uf.dataUrl, uf.name);
      });

      // If there's a note, create a txt file and include it
      if (notiz.trim()) {
        const notizBlob = new Blob(
          [`Notiz vom ${new Date().toLocaleString('de-DE')}\n\n${notiz.trim()}`],
          { type: 'text/plain' },
        );
        const notizFile = new File(
          [notizBlob],
          `Notiz_${new Date().toISOString().slice(0, 10)}.txt`,
          { type: 'text/plain' },
        );
        filesToUpload.push(notizFile);
      }

      setUploadProgress('Wird hochgeladen...');

      const sharedDrive = getEnv('VITE_GOOGLE_SHARED_DRIVE_NAME');

      const response = await uploadFilesToDrive(
        filesToUpload,
        dealName,
        ['Dokumente', 'Allgemeine Uploads'],
        sharedDrive,
      );

      if (response.status) {
        // Save to history
        const entry: UploadHistoryEntry = {
          id: `upload-${Date.now()}`,
          timestamp: new Date().toISOString(),
          notiz: notiz.trim(),
          fileCount: pendingFiles.length,
          files: response.files ?? [],
        };
        const updatedHistory = [entry, ...history];
        setHistory(updatedHistory);
        saveHistory(dealName, updatedHistory);

        // Reset state
        setPendingFiles([]);
        setNotiz('');
        showToast('success', `${pendingFiles.length} Datei(en) hochgeladen`, 3000);
      } else {
        const errorMsg = response.errors?.join(', ') || 'Upload fehlgeschlagen';
        showToast('error', errorMsg, 4000);
      }
    } catch (err: any) {
      console.error('Drive Upload fehlgeschlagen:', err);
      showToast('error', err.message || 'Upload fehlgeschlagen', 4000);
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  };

  // Format timestamp for display
  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 mr-3 mt-0.5 text-blue-600 dark:text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            Hier kannst du Fotos und Dateien hochladen, die keiner bestimmten Phase zugeordnet sind.
            Alle Uploads landen im Google Drive Ordner des Projekts.
          </p>
        </div>
      </div>

      {/* Upload Area */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Neuer Upload
        </h2>

        {/* Notiz-Feld */}
        <div>
          <label
            htmlFor="upload-notiz"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Notiz (optional)
          </label>
          <textarea
            id="upload-notiz"
            value={notiz}
            onChange={(e) => setNotiz(e.target.value)}
            placeholder="z.B. Riss im Fundament, Hausrueckseite..."
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm resize-none"
          />
        </div>

        {/* Pending Files Preview */}
        {pendingFiles.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {pendingFiles.length} Datei(en) ausgewaehlt
            </p>
            <div className="grid grid-cols-3 gap-2">
              {pendingFiles.map((file) => (
                <div key={file.id} className="relative group">
                  <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600">
                    {file.type.startsWith('image/') ? (
                      <img
                        src={file.dataUrl}
                        alt={file.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {/* Remove button */}
                  <button
                    onClick={() => removePendingFile(file.id)}
                    className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Entfernen"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
                    {file.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-3">
          {/* Add Files Button */}
          <button
            onClick={() => setShowUploadModal(true)}
            disabled={uploading}
            className="w-full flex items-center justify-center py-3 px-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors disabled:opacity-50"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {pendingFiles.length > 0 ? 'Weitere Fotos hinzufuegen' : 'Fotos aufnehmen oder auswaehlen'}
          </button>

          {/* Upload Button */}
          {pendingFiles.length > 0 && (
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="w-full py-3 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors flex items-center justify-center"
            >
              {uploading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {uploadProgress}
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  {pendingFiles.length} Datei(en) hochladen
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Upload History */}
      {history.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            Bisherige Uploads
          </h2>
          <div className="space-y-3">
            {history.map((entry) => (
              <div
                key={entry.id}
                className="flex items-start p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
              >
                <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0 mr-3">
                  <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {entry.fileCount} Datei(en)
                    </p>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatTime(entry.timestamp)}
                    </span>
                  </div>
                  {entry.notiz && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 truncate">
                      {entry.notiz}
                    </p>
                  )}
                  {entry.files.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {entry.files
                        .filter(f => !f.fileName.endsWith('.txt'))
                        .map((f) => (
                        <span
                          key={f.fileId}
                          className="inline-flex items-center text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 px-2 py-0.5 rounded"
                        >
                          {f.fileName}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FileUploadModal */}
      <FileUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleFilesSelected}
        title="Fotos hinzufuegen"
        allowedTypes={['image/jpeg', 'image/png']}
        multiUpload={true}
      />
    </div>
  );
};

export default AllgemeineUploads;
